<template>
  <fieldset class="u-spaceItems2">
    <legend>
      <h3>Group <abbr title="Group Taxpayer Identification Number">TIN</abbr> Search</h3>
    </legend>

    <div class="InputGroup js-InputGroup">
      <input
        v-model="tinSearchField.value.value"
        @input="handleTinInput"
        @blur="tinSearchField.handleBlur"
        id="groupTinSearch"
        name="groupTinSearch"
        class="Input InputGroup-cell is-required"
        :class="{ 'is-invalid': tinSearchField.errorMessage.value }"
        placeholder="xx-xxxxxxx"
        type="text"
        required
        v-the-mask="'##-#######'"
        pattern="[0-9]{2}-[0-9]{7}"
        :disabled="isLoading"
      />
      <button
        v-if="formStore.tinSearchResults.confirmedTin"
        @click="clearTinSearch"
        type="button"
        id="clearTinButton"
        class="InputGroup-cell Button Button--subtle"
        title="Clear TIN Search"
      >
        <span class="u-hiddenVisually">Clear</span>
        <span class="u-textRed">
          <svg
            class="Icon Icon--block u-textRed u-flexExpandSides"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <path
              d="M18.01,20.132,3.868,5.99A1.5,1.5,0,0,1,5.99,3.868L20.132,18.01a1.5,1.5,0,1,1-2.121,2.121Z"
            />
            <path
              d="M3.868,20.132a1.5,1.5,0,0,1,0-2.121L18.01,3.868A1.5,1.5,0,1,1,20.132,5.99L5.99,20.132A1.5,1.5,0,0,1,3.868,20.132Z"
            />
          </svg>
        </span>
      </button>
      <button
        v-else
        id="tinSearchButton"
        type="button"
        class="InputGroup-cell Button Button--primary"
        :disabled="!isSearchValid || isLoading"
        @click="performSearch"
      >
        <span v-if="isLoading" class="u-hiddenVisually">Searching...</span>
        <span v-else class="u-hiddenVisually">Search</span>
        <span v-if="isLoading">
          <svg
            class="Icon Icon--block u-textColorInherit u-textGrow1 u-spin"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
              fill="none"
              opacity="0.25"
            />
            <path
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              fill="currentColor"
            />
          </svg>
        </span>
        <span v-else>
          <svg
            class="Icon Icon--block u-textColorInherit u-textGrow1"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <path
              d="M21.55092,18.92019l-4.78362-4.78362A7.78383,7.78383,0,0,0,17.904,10.07323,8.24973,8.24973,0,0,0,9.8305,2.001,7.82876,7.82876,0,0,0,2,9.828v.00347a8.25036,8.25036,0,0,0,8.07227,8.07227,7.78477,7.78477,0,0,0,3.9431-1.06837l4.80893,4.81146a1.20635,1.20635,0,0,0,1.70382,0l1.19369-1.19369C22.19144,19.9835,22.02055,19.38982,21.55092,18.92019ZM4.41016,9.83145a5.42041,5.42041,0,0,1,5.41922-5.4216H9.8305a5.84163,5.84163,0,0,1,5.66337,5.66337,5.42231,5.42231,0,0,1-5.4216,5.4216A5.84319,5.84319,0,0,1,4.41016,9.83145Z"
            />
          </svg>
        </span>
      </button>
    </div>

    <!-- Help Text -->
    <small class="u-block u-textShrink2 u-textGrey u-spaceTop06" id="groupTINHelp">
      Format: 12-3456789
    </small>

    <!-- Validation error message -->
    <p
      v-if="tinSearchField.errorMessage.value"
      class="u-textShrink1 u-textRed u-spaceTop02"
      role="alert"
    >
      {{ tinSearchField.errorMessage.value }}
    </p>

    <!-- API Error message -->
    <p v-if="searchError" class="u-textShrink1 u-textRed u-spaceTop02" role="alert">
      {{ searchError }}
    </p>

    <!-- Search Results -->

    <!-- No Results Message -->
    <div v-if="hasSearched && (!searchResults || searchResults.length === 0)" class="u-spaceTop2">
      <p class="u-textGray">No groups found for TIN: {{ searchedTin }}</p>
    </div>

    <!-- Search Results -->
    <div v-if="showSearchResults">
      <div class="Alert">
        <p class="u-textBlackAlt u-textShrink1">
          This TIN is associated with <strong>{{ formStore.tinSearchResults.groupName }}</strong
          >. Use the filters below to add to this group.
        </p>
      </div>
    </div>
    <!-- Filter and Selection UI -->
    <div v-if="showSearchResults">
      <p class="u-textBlackAlt u-spaceBottom04">
        What information do you need to add to
        <span>{{
          formStore.tinSearchResults.groupName ? formStore.tinSearchResults.groupName : 'this group'
        }}</span
        >?
        <span class="u-hiddenVisually">(required)</span>
        <sup class="u-textRed">*</sup>
      </p>
      <ul id="addToGroupFiltersList" class="u-listReset u-spaceItems03">
        <li>
          <label class="Toggle Toggle--checkbox">
            <input
              class="Toggle-input"
              type="checkbox"
              v-model="formStore.tinSearchResults.options.practices"
              name="addToGroupFilters"
              id="addToGroupPractices"
              required
              :disabled="formStore.tinSearchResults.options.locations === false"
            />
            <span class="Toggle-indicator"></span>
            <span class="Toggle-label">Practices</span>
          </label>
        </li>
        <li>
          <label class="Toggle Toggle--checkbox">
            <input
              class="Toggle-input"
              type="checkbox"
              v-model="formStore.tinSearchResults.options.locations"
              name="addToGroupFilters"
              id="addToGroupLocations"
            />
            <span class="Toggle-indicator"></span>
            <span class="Toggle-label">Locations</span>
          </label>
        </li>
        <li>
          <label class="Toggle Toggle--checkbox">
            <input
              class="Toggle-input"
              type="checkbox"
              v-model="formStore.tinSearchResults.options.providers"
              name="addToGroupFilters"
              id="addToGroupProviders"
            />
            <span class="Toggle-indicator"></span>
            <span class="Toggle-label">Providers</span>
          </label>
        </li>
      </ul>
      <small class="u-textBlackAlt u-block u-spaceTop04"
        >Select all that apply. To add a practice, a location must be added as well.</small
      >
    </div>
  </fieldset>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useField } from 'vee-validate'
import { mask as vTheMask } from 'vue-the-mask'
import { useFormStore } from '../../stores/formStore'

const formStore = useFormStore()

// Define emits
const emit = defineEmits(['validity-changed', 'group-selected'])

// Initialize component state on mount
onMounted(() => {
  // Sync local state with store if we're returning to this component
  if (formStore.tinSearchResults.confirmedTin) {
    hasSearched.value = true
    searchedTin.value = formStore.tinSearchResults.confirmedTin

    // If we have data in the store, populate local searchResults
    if (formStore.tinSearchResults.data && formStore.tinSearchResults.data.length > 0) {
      const transformedResults = formStore.tinSearchResults.data.map((practice) => ({
        tin: formStore.tinSearchResults.confirmedTin,
        groupName: practice.PracticeName,
        practiceId: practice.PracticeId,
        address: practice.PracticeLocations?.[0]?.LocationName || '',
        locations: practice.PracticeLocations || [],
        rawData: practice,
      }))
      searchResults.value = transformedResults
    }
  }

  console.log('GroupTinSearchComponent mounted, store state:', {
    tin: formStore.tinSearchResults.tin,
    confirmedTin: formStore.tinSearchResults.confirmedTin,
    hasData: Boolean(formStore.tinSearchResults.data.length),
  })
})

// Reactive state
const isLoading = ref(false)
const searchError = ref('')
const searchResults = ref([])
const hasSearched = ref(false)
const searchedTin = ref('')
const previousTinValue = ref(formStore.tinSearchResults.tin || '')

// TIN search field validation with vee-validate
const tinSearchField = useField(
  'groupTinSearch',
  (value) => {
    if (!value || value.trim() === '') return 'TIN is required'
    if (!/^\d{2}-\d{7}$/.test(value)) return 'TIN must be in format: 12-3456789'
    return true
  },
  {
    initialValue: formStore.tinSearchResults.tin || '',
    validateOnBlur: true,
    validateOnChange: false,
    validateOnInput: false,
  },
)

// Computed property for search button validity (just TIN field validation)
const isSearchValid = computed(() => {
  return tinSearchField.meta.valid && !isLoading.value
})

// Computed property for overall component validity (step completion)
const isValid = computed(() => {
  // For 'add' applications, check that search is completed and options are selected
  if (formStore.applicationType === 'add') {
    const searchCompleted = Boolean(formStore.tinSearchResults.confirmedTin)
    const hasOptions =
      formStore.tinSearchResults.options.practices ||
      formStore.tinSearchResults.options.locations ||
      formStore.tinSearchResults.options.providers

    return isSearchValid.value && searchCompleted && hasOptions
  }

  // For 'new' applications, just need valid TIN field
  return isSearchValid.value
})

// Computed property for showing search results
const showSearchResults = computed(() => {
  return (
    formStore.tinSearchResults.tin &&
    formStore.tinSearchResults.confirmedTin &&
    formStore.tinSearchResults.isLoading == false
  )
})

// Custom input handler - only for store updates and resets when value changes
const handleTinInput = (event) => {
  const newValue = event.target.value

  // Update store
  formStore.tinSearchResults.tin = newValue || ''

  // Only reset if:
  // 1. We have actually searched before (hasSearched is true)
  // 2. The TIN value actually changed from what we previously had
  // 3. The change is significant (not just whitespace or formatting)
  const significantChange =
    newValue.replace(/\D/g, '') !== previousTinValue.value.replace(/\D/g, '')

  if (hasSearched.value && significantChange && previousTinValue.value !== '') {
    console.log('TIN changed significantly, resetting search results')
    formStore.tinSearchResults.confirmedTin = ''
    formStore.tinSearchResults.groupName = ''
    formStore.tinSearchResults.selectedGroup = ''
    formStore.tinSearchResults.selectedPractice = ''
    formStore.tinSearchResults.data = []
    formStore.tinSearchResults.error = null
    formStore.tinSearchResults.isLoading = false

    // Reset local state
    searchResults.value = []
    hasSearched.value = false
    searchError.value = ''
  }

  // Update the previous value tracker
  previousTinValue.value = newValue
}

// Clear TIN search and reset all related data
const clearTinSearch = () => {
  // Reset form field
  tinSearchField.value.value = ''
  tinSearchField.handleBlur()

  // Reset all tinSearchResults in store
  formStore.tinSearchResults.tin = ''
  formStore.tinSearchResults.confirmedTin = ''
  formStore.tinSearchResults.groupName = null
  formStore.tinSearchResults.isLoading = false
  formStore.tinSearchResults.error = null
  formStore.tinSearchResults.data = []
  formStore.tinSearchResults.options.practices = false
  formStore.tinSearchResults.options.locations = false
  formStore.tinSearchResults.options.providers = false

  // Reset local component state
  searchResults.value = []
  hasSearched.value = false
  searchError.value = ''
  searchedTin.value = ''
  previousTinValue.value = ''

  console.log('TIN search cleared and reset')
}

// Check if running on localhost for test data
const isLocalhost = () => {
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === ''
  )
}

// Test data fallback for localhost
const getTestData = () => {
  return {
    success: true,
    responseText: {
      result: JSON.stringify([
        {
          PracticeId: 1275,
          PracticeName: 'Practice Name 3',
          PracticeLocations: [
            {
              LocationId: 3115,
              LocationName: 'Location Name 1 | Location 1 Address 1 | ',
            },
          ],
        },
        {
          PracticeId: 1276,
          PracticeName: 'Test Practice 2',
          PracticeLocations: [
            {
              LocationId: 3116,
              LocationName: 'Location Name 2 | Location 2 Address 2 | ',
            },
            {
              LocationId: 3117,
              LocationName: 'Location Name 3 | Location 3 Address 3 | ',
            },
          ],
        },
      ]),
    },
    groupName: 'Test Group From Group Lookup',
  }
}

// Modern API search function - combines both group lookup and practice data fetching
const performSearch = async () => {
  if (!isSearchValid.value) return

  const tin = tinSearchField.value.value

  // If we're searching for the same TIN and already have results, don't clear them
  const isSameTin = tin === searchedTin.value
  const hasExistingResults = searchResults.value.length > 0

  isLoading.value = true
  searchError.value = ''

  // Only clear results if it's a different TIN or we don't have existing results
  if (!isSameTin || !hasExistingResults) {
    searchResults.value = []
  }

  searchedTin.value = tin
  formStore.tinSearchResults.isLoading = true

  try {
    let groupLookupData
    let practiceData

    // Use test data for localhost, real API for production
    if (isLocalhost()) {
      console.log('Using test data for localhost')
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Mock both API calls
      groupLookupData = {
        success: true,
        responseText: 'Test Group From Group Lookup',
      }

      practiceData = getTestData()
    } else {
      // Step 1: Group lookup to validate TIN
      const groupLookupResponse = await fetch('/group-lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify({ groupTin: tin }),
      })

      if (!groupLookupResponse.ok) {
        throw new Error(
          `Group lookup failed: ${groupLookupResponse.status} ${groupLookupResponse.statusText}`,
        )
      }

      groupLookupData = await groupLookupResponse.json()

      // Step 2: If group lookup successful, fetch practice data
      if (groupLookupData.success === true) {
        const practiceResponse = await fetch('/LegalEntity-Practice-Info', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=UTF-8',
          },
          body: JSON.stringify({ groupTin: tin }),
        })

        if (!practiceResponse.ok) {
          throw new Error(
            `Practice data fetch failed: ${practiceResponse.status} ${practiceResponse.statusText}`,
          )
        }

        practiceData = await practiceResponse.json()
      }
    }

    // Process the group lookup response
    if (groupLookupData.success === true) {
      // Store the confirmed TIN in form store
      formStore.tinSearchResults.confirmedTin = tin
      formStore.tinSearchResults.groupName = groupLookupData.responseText

      // Process practice data if available
      if (practiceData && practiceData.success === true) {
        const groupDataArray = JSON.parse(practiceData.responseText.result)
        console.log('Group data received:', groupDataArray)

        // Transform the data to match our component's expected format
        const transformedResults = groupDataArray.map((practice) => ({
          tin: tin, // Use the searched TIN
          groupName: practice.PracticeName,
          practiceId: practice.PracticeId,
          address: practice.PracticeLocations?.[0]?.LocationName || '',
          locations: practice.PracticeLocations || [],
          rawData: practice, // Keep original data for further processing
        }))

        // Update store with results
        formStore.tinSearchResults.data = groupDataArray // Store original format
        formStore.tinSearchResults.error = null

        searchResults.value = transformedResults
        hasSearched.value = true

        if (searchResults.value.length === 0) {
          searchError.value = `No practices found for TIN: ${tin}`
        }
      } else {
        // Group exists but no practice data
        searchResults.value = []
        hasSearched.value = true
        searchError.value = `TIN validated but no practice data available for: ${tin}`
      }
    } else {
      throw new Error(groupLookupData.responseText || 'Group lookup was not successful')
    }
  } catch (error) {
    console.error('TIN search error:', error)
    searchError.value = error.message || 'Search failed. Please try again.'
    formStore.tinSearchResults.error = error.message
    formStore.tinSearchResults.confirmedTin = null
    hasSearched.value = true
  } finally {
    isLoading.value = false
    formStore.tinSearchResults.isLoading = false
  }
}

// Handle group selection
const selectGroup = (group) => {
  // Update store with selected group
  formStore.tinSearchResults.groupName = group.groupName
  formStore.tinSearchResults.selectedGroup = group
  formStore.tinSearchResults.selectedPractice = group.rawData // Store the original practice data

  // Store practices and locations for form population
  formStore.practices = formStore.tinSearchResults.data || []

  // Emit selection event to parent with both transformed and raw data
  emit('group-selected', {
    selected: group,
    practices: formStore.tinSearchResults.data,
    tin: group.tin,
  })

  console.log('Selected group:', group)
  console.log('Available practices:', formStore.tinSearchResults.data)
}

// Watch field validity and emit changes
watch(
  isValid,
  (valid) => {
    emit('validity-changed', valid)
  },
  { immediate: true },
)

// Watch field value changes for store sync (without triggering validation)
watch(
  () => tinSearchField.value.value,
  (newValue) => {
    // Only update store, don't trigger validation or resets
    formStore.tinSearchResults.tin = newValue || ''
  },
)

// Watch specifically for locations changes to enforce business rule
watch(
  () => formStore.tinSearchResults.options.locations,
  (newLocationsValue, oldLocationsValue) => {
    console.log('Locations changed from', oldLocationsValue, 'to', newLocationsValue)

    // If locations is unchecked and practices is still checked, uncheck practices
    if (
      oldLocationsValue === true &&
      newLocationsValue === false &&
      formStore.tinSearchResults.options.practices
    ) {
      console.log('Locations unchecked - automatically unchecking practices')
      formStore.tinSearchResults.options.practices = false
    }
  },
)

// Watch for changes to confirmed TIN and trigger data population
watch(
  () => formStore.tinSearchResults.confirmedTin,
  (newTin) => {
    if (newTin && formStore.tinSearchResults.data.length > 0) {
      console.log('TIN confirmed, triggering practice structure update')
      // Populate data from TIN search results
      formStore.populateFromTinSearch()
    }
  },
)

// Watch for changes to options and trigger data population
watch(
  () => formStore.tinSearchResults.options,
  (newOptions) => {
    if (newOptions && (newOptions.practices || newOptions.locations || newOptions.providers)) {
      console.log('TIN search options changed, triggering practice structure update')
      // Populate data from TIN search results
      formStore.populateFromTinSearch()
    }
  },
  { deep: true },
)
</script>

<style scoped>
.search-results {
  max-height: 400px;
  overflow-y: auto;
}

.search-result-item {
  border: 1px solid #e0e0e0;
  background-color: #f9f9f9;
}

.u-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
